import { Injectable, NgModule } from '@angular/core';
import {
  RouterModule,
  RouterStateSnapshot,
  Routes,
  TitleStrategy,
} from '@angular/router';
import {
  AuthGuard,
  AuthLoginGuard,
  IsLoggedInGuard,
} from './core/gaurds/auth.guard';
import { PageNotFoundComponent } from './core/components/page-not-found/page-not-found.component';
import { Title } from '@angular/platform-browser';
import { ResetPasswordComponent } from './core/components/reset-password/reset-password.component';
import { VerifyEmailComponent } from './core/components/verify-email/verify-email.component';
import { AppDownloadComponent } from './core/components/app-download/app-download.component';
import {
  ADMIN,
  LOGIN,
  LOGOUT,
  RESET_PASSWORD,
  SYNOPSIS,
  TRACE,
  VERIFY_EMAIL,
  DRIVER,
  PANAROMA,
  UNAUTH,
  LIVE_SHARE,
  ALERTS,
  TRIP,
  COMMAND,
  PROFILE,
  ASSISTANCE_HUB,
  EV_INTELLIGENCE,
  SAVINGS_CALCULATOR,
  NPS,
  ONEM_INSIGHTS,
  DAAS,
  DAAS_APP,
  DAAS_HOME,
} from './shared/routeLink.constant';
import { LogoutComponent } from './core/components/logout/logout.component';
import { DaasAppGuard, DaasMenuGuard } from './core/gaurds/daas-app.guard';
import { ViewLiveTrackingComponent } from './features/tracking/view-live-tracking/view-live-tracking.component';
import { VehicleTrackingLiveComponent } from './features/tracking/view-live-tracking/vehicle-tracking-live/vehicle-tracking-live.component';
import { CommonService } from './shared/service/common.service';
import { LoginComponent } from './core/components/login/login.component';
import { landingResolver } from './core/services/landing.resolver';
import { DefaultNavigateComponent } from './core/components/default-navigate/default-navigate.component';
import { EvSavingsDialogComponent } from './core/components/login/ev-savings-dialog/ev-savings-dialog.component';

@Injectable()
export class TemplatePageTitleStrategy extends TitleStrategy {
  constructor(private readonly title: Title) {
    super();
  }

  override updateTitle(routerState: RouterStateSnapshot) {
    const title = this.buildTitle(routerState);
    if (title !== undefined) {
      this.title.setTitle(
        `1M by MontraElectric - Streamline Fleet, Amplify Business | ${title}`
      );
    }
  }
}

export const routes: Routes = [
  {
    path: '',
    resolve: {
      landing: landingResolver,
    },
    component: DefaultNavigateComponent,
  },
  {
    path: NPS,
    component: AppDownloadComponent,
    title: 'App Download',
    canActivate: [AuthLoginGuard],
  },
  {
    path: LOGIN,
    component: LoginComponent,
    title: 'Login',
    canActivate: [AuthLoginGuard],
  },
  {
    path: VERIFY_EMAIL,
    component: VerifyEmailComponent,
    title: 'Verify Email',
    canActivate: [AuthLoginGuard],
  },
  {
    path: RESET_PASSWORD,
    component: ResetPasswordComponent,
    title: 'Reset Password',
  },
  {
    path: SAVINGS_CALCULATOR,
    component: EvSavingsDialogComponent,
    title: 'Savings Calculator',
  },
  {
    //Tracking Module
    path: `${TRACE}`,
    loadChildren: () =>
      import('./features/tracking/tracking.module').then(
        (m) => m.TrackingModule
      ),
  },
  {
    //Insights Module
    path: `${ONEM_INSIGHTS}`,
    loadChildren: () =>
      import('./features/insight/insight.module').then((m) => m.InsightModule),
  },
  {
    //Reports Module
    path: `${SYNOPSIS}`,
    loadChildren: () =>
      import('./features/reports/report.module').then((m) => m.ReportModule),
  },
  {
    //ALERTS Module
    path: `${ALERTS}`,
    loadChildren: () =>
      import('./features/alerts/alerts.module').then((m) => m.AlertsModule),
  },
  {
    // DAAS React app (1mdaas-ui) loaded as an MFE, opened from "Open DAAS" on admin/daas.
    // Declared here, before the admin module, so the admin AuthGuard (menu URLs only) does not apply.
    path: `${ADMIN}/${DAAS}/${DAAS_APP}`,
    loadComponent: () =>
      import('./features/admin/daas/daas-app/daas-app.component').then(
        (m) => m.DaasAppComponent
      ),
    title: 'DAAS',
    canActivate: [DaasAppGuard],
  },
  {
    // DAAS top-level menu: each DAAS submenu opens its 1mdaas-ui page at /daas/<page>/...
    path: DAAS,
    children: [
      { path: '', pathMatch: 'full', redirectTo: DAAS_HOME },
      {
        // One route for every DAAS page, so the MFE stays mounted while the user moves between them
        path: '**',
        loadComponent: () =>
          import('./features/admin/daas/daas-app/daas-app.component').then(
            (m) => m.DaasAppComponent
          ),
        title: 'DAAS',
        data: { daasMenu: true },
        canActivate: [DaasMenuGuard],
        // Re-check access on every DAAS page, not only when the route is first entered
        runGuardsAndResolvers: 'pathParamsChange',
      },
    ],
  },
  {
    //Admin Module
    path: `${ADMIN}`,
    loadChildren: () =>
      import('./features/admin/admin.module').then((m) => m.AdminModule),
    canActivate: [AuthGuard],
  },
  {
    //Driver Module
    path: `${DRIVER}`,
    loadChildren: () =>
      import('./features/drivers/drivers.module').then((m) => m.DriversModule),
    canActivate: [AuthGuard],
  },
  {
    //Panorama Module
    path: `${PANAROMA}`,
    loadChildren: () =>
      import('./features/panorama/panorama.module').then(
        (m) => m.PanoramaModule
      ),
    canActivate: [AuthGuard],
  },
  {
    //Trip Module
    path: `${TRIP}`,
    loadChildren: () =>
      import('./features/trips/trip.module').then((m) => m.TripModule),
    canActivate: [AuthGuard],
  },
  {
    //COMMAND_CENTER Module
    path: `${COMMAND}`,
    loadChildren: () =>
      import('./features/command-center/command-center.module').then(
        (m) => m.CommandCenterModule
      ),
  },
  {
    //Profile Module
    path: `${PROFILE}`,
    loadChildren: () =>
      import('./features/profile/profile.module').then((m) => m.ProfileModule),
    canActivate: [IsLoggedInGuard],
  },
  {
    path: EV_INTELLIGENCE,
    loadChildren: () =>
      import('./features/ev-intelligence/ev-intelligence.module').then(
        (m) => m.EvIntelligenceModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: `${ASSISTANCE_HUB}`,
    loadChildren: () =>
      import('./features/assistance-hub/assistance-hub.module').then(
        (m) => m.AssistanceHubModule
      ),
  },
  {
    path: `${LIVE_SHARE}/:vehicleTrackerKey`,
    component: ViewLiveTrackingComponent,
    pathMatch: 'full',
  },
  {
    path: `${LIVE_SHARE}/:vehicleTrackerKey/:vehicleNumber`,
    component: VehicleTrackingLiveComponent,
    pathMatch: 'full',
  },
  {
    path: LOGOUT,
    component: LogoutComponent,
    title: 'Log out',
  },
  {
    path: UNAUTH,
    pathMatch: 'full',
    component: PageNotFoundComponent,
    canActivate: [IsLoggedInGuard],
  },
  {
    path: '**',
    pathMatch: 'full',
    component: PageNotFoundComponent,
    canActivate: [AuthGuard],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'top',
    }),
  ],
  exports: [RouterModule],
  providers: [
    CommonService,
    {
      provide: TitleStrategy,
      useClass: TemplatePageTitleStrategy,
    },
  ],
})
export class AppRoutingModule {}
